/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, createContext } from 'react';
import { Alert } from '@one-thd/sui-atomic-components';

// type: success, warning, error, info
// https://brand.homedepot.com/site/knapsack-nucleus/latest/pattern/alerts?subPageId=overview

const initialState = {};

interface IAlertState {
  [type: string]: {
    message: string | JSX.Element;
    onClose?: () => void;
    size?: 'small' | 'large';
    headline?: string;
    hideCloseIcon?: boolean;
  };
}

interface IAlertTypeProps {
  [type: string]: {
    message: string | JSX.Element;
    onClose?: () => void;
    size?: 'small' | 'large';
    headline?: string;
    hideCloseIcon?: boolean;
  };
}

interface IAlertContextValues {
  showAlert: (_alertState: IAlertTypeProps) => void;
  dismissAlert: (type: string) => void;
  clearAlert: () => void;
}

interface IAlertProviderProps {
  children: React.ReactNode;
}

export const AlertContext = createContext<IAlertContextValues | null>(null);

export const AlertProvider = ({ children }: IAlertProviderProps) => {
  const [alertState, setAlertState] = useState<IAlertState>(initialState);

  const showAlert = (_alertState: IAlertTypeProps) => {
    setAlertState((prevState: IAlertState) => ({
      ...prevState,
      ..._alertState
    }));
  };

  const dismissAlert = (type: string) => {
    const currentAlert = alertState[type];
    setAlertState((prevState: IAlertState) => {
      const _prev = { ...prevState };
      delete _prev[type];
      return _prev;
    });
    if (currentAlert?.onClose) {
      currentAlert.onClose();
    }
  };

  const clearAlert = () => {
    setAlertState(initialState);
  };

  return (
    <AlertContext.Provider
      value={{
        showAlert,
        dismissAlert,
        clearAlert
      }}
    >
      <div className="sui-flex sui-flex-col sui-w-full sui-pb-2">
        {Object.keys(alertState || {}).map((alertType: string) => (
          <Alert
            data-testid={`alert-${alertType}`}
            key={alertType}
            onClose={alertState[alertType]?.hideCloseIcon ? null : () => dismissAlert(alertType)}
            status={alertType}
            size={alertState[alertType]?.size || 'small'}
            headline={alertState[alertType]?.headline}
          >
            {alertState[alertType]?.message}
          </Alert>
        ))}
      </div>
      {children}
    </AlertContext.Provider>
  );
};
